import { faGear } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CoachProfile,
  FlexBox,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { UserRole } from "@joonapp/web-shared"
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

import ClientStatusSegmentedControl from "./ClientStatusSegmentedControl"
import CycleEndCell from "./CycleEndCell"
import { LastMessageCell } from "./LastMessageCell"
import SessionsCell from "./SessionsCell"
import TodosCell from "./TodosCell"
import useClientTable from "./useClientTable"
import CustomTable from "../../../components/table/CustomTable"
import TableHeaderCell from "../../../components/table/TableHeaderCell"
import { useUserQuery } from "../../../networking/user/queries"
import { ClientTableData } from "../../../types/misc"
import { formatDate } from "../../../util/dates"
import useAdminPageStore from "../hooks/useAdminpageStore"

const columnHelper = createColumnHelper<ClientTableData>()

const CoachClientTable = () => {
  const [sorting, setSorting] = useState([{ id: "Last message", desc: true }]) // TODO: update this, and make sorting fns
  const [pagination] = useState({ pageIndex: 0, pageSize: 100 })
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const { debouncedSearchValue } = useAdminPageStore()
  const { data: user } = useUserQuery()

  const navigate = useNavigate()

  const { tableData } = useClientTable()

  const lastMessageDateSortingFn = (
    rowA: Row<ClientTableData>,
    rowB: Row<ClientTableData>
  ) => {
    const dateA = new Date(rowA.original.channel.lastMessage?.createdAt || 0)
    const dateB = new Date(rowB.original.channel.lastMessage?.createdAt || 0)
    return dateA > dateB ? 1 : dateA < dateB ? -1 : 0
  }

  const createColumns = () => [
    columnHelper.accessor((row) => row, {
      id: "Row Number",
      header: () => null,
      cell: (info) => (
        <Typography variant="bodyXSmallBold" color={JoonUIColor.text.secondary}>
          {info.table
            .getSortedRowModel()
            ?.flatRows?.findIndex((flatRow) => flatRow.id === info.row.id) + 1}
        </Typography>
      ),
      size: 10,
    }),
    ...(debouncedSearchValue
      ? [
          columnHelper.accessor(
            (row) => {
              const coachProfile = row.coachingGroup?.profiles.find(
                (p) => p.role === UserRole.COACH && p.user.id !== user?.id
              ) as CoachProfile
              return { coachProfile }
            },
            {
              id: "Coach",
              header: () => <TableHeaderCell>Coach</TableHeaderCell>,
              cell: (info) => (
                <Typography
                  variant="bodySmall"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {info.getValue().coachProfile?.user.name}
                </Typography>
              ),
              size: 20,
            }
          ),
        ]
      : []),
    columnHelper.accessor(
      (row) => {
        const clientProfile = row.coachingGroup?.profiles.find(
          (p) => p.role === UserRole.CLIENT
        )
        return {
          name: clientProfile?.user.name || "N/A",
          clientProfile,
        }
      },
      {
        id: "Client name",
        header: () => <TableHeaderCell>Client name</TableHeaderCell>,
        cell: (info) => (
          <FlexBox direction="column">
            <Typography variant="body">
              {info.getValue().name}{" "}
              {info.getValue().clientProfile?.user.last_name}
            </Typography>
          </FlexBox>
        ),
        size: 150,
      }
    ),
    columnHelper.accessor(
      (row) => {
        const date = row.channel.lastMessage?.createdAt
        const isToday = formatDate(new Date(date || 0)) === "Today"

        const lastMessageSender = row.coachingGroup?.profiles.find(
          // @ts-ignore - sender is on lastMessage
          (p) => p.id === Number(row.channel.lastMessage?.sender?.userId)
        )

        return {
          date: formatDate(new Date(date || 0), "MMM D"),
          time: isToday
            ? formatDate(new Date(date || 0), "h:mm A", false)
            : undefined,
          sender: lastMessageSender,
        }
      },
      {
        id: "Last message",
        header: () => <TableHeaderCell>Last message</TableHeaderCell>,
        cell: LastMessageCell,
        size: 200,
        enableSorting: true,
        sortingFn: lastMessageDateSortingFn,
      }
    ),
    columnHelper.accessor((row) => row.billingPeriods, {
      id: "Cycle end",
      header: () => <TableHeaderCell>Cycle end</TableHeaderCell>,
      cell: CycleEndCell,
      size: 100,
    }),
    columnHelper.accessor((row) => row.billingPeriods, {
      id: "Sessions",
      header: () => <TableHeaderCell>Sessions</TableHeaderCell>,
      cell: SessionsCell,
      size: 200,
    }),
    columnHelper.accessor((row) => row.coachingGroup, {
      id: "Todos",
      header: () => <TableHeaderCell>Todos</TableHeaderCell>,
      cell: TodosCell,
      size: 100,
    }),
    columnHelper.accessor((row) => row.channel, {
      id: "Actions",
      header: () => <TableHeaderCell></TableHeaderCell>,
      cell: (info) => {
        return (
          <FlexBox
            direction="row"
            align="center"
            wrap={false}
            gap={SPACING.space05}
            style={{ opacity: hoveredRowId === info.row.id ? 1 : 0 }}
          >
            <Button
              size="small"
              buttonType="secondary"
              text={<Typography variant="bodySmall">View</Typography>}
              onClick={() => {
                navigate(`/${info.row.original.coachingGroup?.id}/messages`)
              }}
              style={{
                borderRadius: SPACING.space2,
                marginLeft: "auto",
                borderColor: JoonUIColor.border.default,
              }}
            />
            <TextButton
              onClick={() =>
                window.open(
                  `https://app.joonapp.io/admin/accounts/group/${info.row.original.coachingGroup?.id}/change`,
                  "_blank"
                )
              }
            >
              <FontAwesomeIcon
                icon={faGear}
                fontSize="16px"
                color={JoonUIColor.icon.neutral}
              />
            </TextButton>
          </FlexBox>
        )
      },
      size: 36,
    }),
  ]

  const table = useReactTable({
    data: tableData,
    columns: createColumns(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting, pagination },
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <div className="flex flex-col flex-grow h-full w-full max-w-[1200px] p-6">
      <ClientStatusSegmentedControl />
      <CustomTable
        table={table}
        hoveredRowId={hoveredRowId}
        setHoveredRowId={setHoveredRowId}
        disablePagination={true}
        collapseRows={true}
      />
    </div>
  )
}

export default CoachClientTable
